import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button, Field, Flex, Input, Message, Modal, Text } from '@workwhile/ui'

import { useVndlyCandidateIssuesContextValue } from '../context/VndlyCandidateIssuesProvider'
import { useVndlyIssuesContextValue } from 'pages/VndlyIssues/context/VndlyIssuesProvider'

export const linkVndlyCandidateFormSchema = z.object({
  candidateUniqueId: z
    .string()
    .trim()
    .min(1, 'Candidate unique id is required'),
})

export type LinkVndlyCandidateForm = z.infer<
  typeof linkVndlyCandidateFormSchema
>

type LinkVndlyCandidateButtonProps = {
  workerId: number
  workerName: string
  vndlyInstanceId: number
}

export const LinkVndlyCandidateButton = ({
  workerId,
  workerName,
  vndlyInstanceId,
}: LinkVndlyCandidateButtonProps) => {
  const {
    actions: { linkVndlyCandidate, resetLinkVndlyCandidate },
    state: { linkVndlyCandidateMutationState },
  } = useVndlyCandidateIssuesContextValue()

  const {
    state: { instances },
  } = useVndlyIssuesContextValue()

  const instanceUrl =
    instances.find((instance) => instance.id === vndlyInstanceId)
      ?.instanceUrl ?? vndlyInstanceId.toString()

  const [isOpen, setIsOpen] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LinkVndlyCandidateForm>({
    resolver: zodResolver(linkVndlyCandidateFormSchema),
    defaultValues: {
      candidateUniqueId: '',
    },
  })

  useEffect(() => {
    if (!linkVndlyCandidateMutationState.isSuccess) {
      return
    }

    setIsOpen(false)
    reset()
    resetLinkVndlyCandidate()
  }, [
    linkVndlyCandidateMutationState.isSuccess,
    reset,
    resetLinkVndlyCandidate,
  ])

  const handleClick = () => {
    resetLinkVndlyCandidate()
    reset()
    setIsOpen(true)
  }

  const handleCancel = () => {
    resetLinkVndlyCandidate()
    reset()
    setIsOpen(false)
  }

  const onSubmit = handleSubmit((data) => {
    linkVndlyCandidate({
      workerId,
      vndlyInstanceId,
      candidateUniqueId: data.candidateUniqueId,
    })
  })

  return (
    <>
      <Button
        variant="secondary"
        kind="small"
        onClick={handleClick}
        style={{ padding: '0 0.5rem' }}
        disabled={linkVndlyCandidateMutationState.isPending}
        loading={linkVndlyCandidateMutationState.isPending}
      >
        <Text fontSize="12px">Link candidate</Text>
      </Button>
      {isOpen ? (
        <Modal
          open
          onClose={handleCancel}
          title={`Link candidate for ${workerName}`}
        >
          <Text mb={2}>
            Enter the VNDLY candidate unique id to link {workerName} ({workerId}
            ) on instance {instanceUrl}.
          </Text>
          {linkVndlyCandidateMutationState.isError &&
          linkVndlyCandidateMutationState.errorMessage ? (
            <Message variant="error" mb={3}>
              {linkVndlyCandidateMutationState.errorMessage}
            </Message>
          ) : null}
          <form onSubmit={onSubmit}>
            <Field
              label="VNDLY candidate unique id (e.g. AB00000123)"
              error={errors.candidateUniqueId?.message}
            >
              <Input {...register('candidateUniqueId')} />
            </Field>
            <Flex flexDirection="row" justifyContent="space-between" gap={2}>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={linkVndlyCandidateMutationState.isPending}
                disabled={linkVndlyCandidateMutationState.isPending}
              >
                Submit
              </Button>
            </Flex>
          </form>
        </Modal>
      ) : null}
    </>
  )
}
